import {
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState
} from 'react'
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
  useWatch
} from 'react-hook-form'

import {
  AuthenticationFormValues,
  getExternalIntegrationForObservabilityTool,
  ObservabilityToolDomain,
  getAWSDefaultValuesForReset,
  setObservabilityToolAndDomain
} from 'components/status-checks/authentication-form-utils'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  ObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useTranslation } from 'hooks/use-translation'

interface AWSAccountAutocompleteOption {
  id: string
  label: string
}

enum AWSAccountSelectRadioValue {
  ExistingDomains = 'ExistingDomains',
  NewDomain = 'NewDomain'
}

export const AWSAuthenticationAccountSelect: React.FC<{
  domains: ObservabilityToolDomain[]
  isAuthenticationValid: boolean
  isCreatingNewAccount: boolean
  setIsAuthenticationValid: Dispatch<SetStateAction<boolean>>
  setIsCreatingNewAccount: Dispatch<SetStateAction<boolean>>
}> = ({ domains, isCreatingNewAccount, setIsCreatingNewAccount }) => {
  const { trans } = useTranslation()
  const form = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()
  const { control, setValue, trigger, reset } = form
  const [selectedObservabilityTool, domain] = useWatch<
    StatusCheckFormValues | AuthenticationFormValues
  >({
    control,
    name: ['observabilityTool', 'domain']
  })
  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()

  const { setAuthenticationResponse } = useContext(
    ObservabilityToolAuthenticationContext
  )

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const [radioValue, setRadioValue] = useState<AWSAccountSelectRadioValue>(
    AWSAccountSelectRadioValue.ExistingDomains
  )
  // Holds the selected AWS Account value in state, allowing for freezing that value locally when creating a new account
  const [selectedAWSAccountOption, setSelectedAWSAccountOption] =
    useState<AWSAccountAutocompleteOption>({
      id: domain?.name || domains?.[0]?.name || '',
      label: domain?.name || domains?.[0]?.name || ''
    })

  // Invoked when the user selects the "Add New Account" radio button
  const handleResetAWSDomain = async (
    field: ControllerRenderProps<
      StatusCheckFormValues | AuthenticationFormValues,
      'domain.name'
    >
  ) => {
    reset(getAWSDefaultValuesForReset(externalIntegrations))
    await trigger('headers')

    field.onChange('')
  }

  const handleSelectAWSDomain = async (
    domain: string,
    field: ControllerRenderProps<
      StatusCheckFormValues | AuthenticationFormValues,
      'domain.name'
    >
  ) => {
    const observabilityTool = selectedObservabilityTool

    const selectedDomain = observabilityTool?.domains.find(
      (d) => d.name === domain
    )

    const newObservabilityTool = {
      ...observabilityTool,
      integrationType: selectedDomain?.integrationType || null
    }

    const newObservabilityToolWithSelectedDomain = {
      ...newObservabilityTool,
      selectedDomain: domain
    }

    const externalIntegration = getExternalIntegrationForObservabilityTool(
      newObservabilityToolWithSelectedDomain,
      externalIntegrations
    )

    setObservabilityToolAndDomain(
      newObservabilityToolWithSelectedDomain,
      selectedDomain,
      externalIntegration,
      form
    )

    // Validate headers whenever domain changes
    await trigger('headers')

    field.onChange(domain)
  }

  const options: AWSAccountAutocompleteOption[] = useMemo(() => {
    if (domains?.length > 0) {
      return domains.map((domain) => ({
        label: domain.name,
        id: domain.name
      }))
    }

    return []
  }, [domains])

  const handleOnRadioChange = async (
    _: React.ChangeEvent<HTMLInputElement>,
    value: AWSAccountSelectRadioValue,
    field: ControllerRenderProps<
      StatusCheckFormValues | AuthenticationFormValues,
      'domain.name'
    >
  ) => {
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)

    if (value === AWSAccountSelectRadioValue.ExistingDomains) {
      setValue('domain.name', selectedAWSAccountOption.label)
      setValue(
        'observabilityTool.selectedDomain',
        selectedAWSAccountOption.label
      )
      await handleSelectAWSDomain(selectedAWSAccountOption.label, field)
      setIsCreatingNewAccount(false)
    }

    if (value === AWSAccountSelectRadioValue.NewDomain) {
      setIsCreatingNewAccount(true)
      await handleResetAWSDomain(field)
    }

    setRadioValue(value)
  }

  const handleOnAutocompleteChange = async (
    value: AWSAccountAutocompleteOption,
    field: ControllerRenderProps<
      StatusCheckFormValues | AuthenticationFormValues,
      'domain.name'
    >
  ) => {
    setSelectedAWSAccountOption(value)
    if (!isCreatingNewAccount) {
      await handleSelectAWSDomain(value.label, field)
    }
  }

  return (
    <Controller
      control={control}
      name="domain.name"
      render={({ field }) => {
        return (
          <RadioGroup
            data-testid="aws-account-select-radio-group"
            name="aws-domain-selection"
            value={radioValue}
            onChange={(_, value: AWSAccountSelectRadioValue) =>
              handleOnRadioChange(_, value, field)
            }
          >
            <Stack rowGap={2}>
              <FormControlLabel
                control={<Radio />}
                label={
                  <Autocomplete<AWSAccountAutocompleteOption, false, true>
                    {...field}
                    disableClearable
                    disabled={
                      radioValue !== AWSAccountSelectRadioValue.ExistingDomains
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={options}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label={trans(
                          'forms.save_status_checks.authentication.aws.aws_account_id.text_field.label'
                        )}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    value={selectedAWSAccountOption}
                    onChange={(_, value: AWSAccountAutocompleteOption) =>
                      handleOnAutocompleteChange(value, field)
                    }
                  />
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    width: '100%'
                  }
                }}
                value={AWSAccountSelectRadioValue.ExistingDomains}
              />
              <FormControlLabel
                control={<Radio />}
                label={
                  <Typography>
                    {trans(
                      'forms.save_status_checks.authentication.aws.add_new_account.text_field.label'
                    )}
                  </Typography>
                }
                value={AWSAccountSelectRadioValue.NewDomain}
              />
            </Stack>
          </RadioGroup>
        )
      }}
    />
  )
}
